import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, HandledError } from '@synergy/api';
import { Dispatch } from 'redux';
import { FormikErrors } from 'formik';
import { Locale, Media } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { BankAccount } from './../models/bank-accounts';
import { BankAccountErrorCode } from '../../../../models/bank-account-error-code';
import { BankAccountFormValues } from './../../../../models/bank-account-form-values';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { SaveResult } from '../../../../../../common/models/save-result';
import { SupplierDetail } from '../models/supplier-detail';
import { SupplierDetailOverviewFormValues } from '../models/supplier-detail-overview-form-values';
import { WhitelistedCustomer } from './../models/whitelisted-customer';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { getCountry } from './../../../../utils/get-country-values';
import { serverErrorNotification } from '../../../../../../common/utils/server-error-notification';
import { supplierDetailOverviewRequestSelector } from './supplier-detail-overview-selectors';

export const SUPPLIER_DETAIL_BANK_ACCOUNT_ADD = 'SUPPLIER_DETAIL_BANK_ACCOUNT_ADD';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_CREATED = 'SUPPLIER_DETAIL_BANK_ACCOUNT_CREATED';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE = 'SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED = 'SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_UPDATED = 'SUPPLIER_DETAIL_BANK_ACCOUNT_UPDATED';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE = 'SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED = 'SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED';
export const SUPPLIER_DETAIL_OVERVIEW_ERROR = 'SUPPLIER_DETAIL_OVERVIEW_ERROR';
export const SUPPLIER_DETAIL_BANK_ACCOUNT_ERROR = 'SUPPLIER_DETAIL_BANK_ACCOUNT_ERROR';
export const SUPPLIER_DETAIL_OVERVIEW_RECEIVE_DETAILS = 'SUPPLIER_DETAIL_OVERVIEW_RECEIVE_DETAILS';
export const SUPPLIER_DETAIL_OVERVIEW_REQUEST_DETAILS = 'SUPPLIER_DETAIL_OVERVIEW_REQUEST_DETAILS';
export const SUPPLIER_DETAIL_OVERVIEW_SAVE = 'SUPPLIER_DETAIL_OVERVIEW_SAVE';
export const SUPPLIER_DETAIL_OVERVIEW_UPDATE = 'SUPPLIER_DETAIL_OVERVIEW_UPDATE';
export const SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_FINISHED = 'SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_FINISHED';
export const SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_IMAGE = 'SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_IMAGE';
export const SUPPLIER_DETAIL_OVERVIEW_REMOVED_PROFILE_IMAGE = 'SUPPLIER_DETAIL_OVERVIEW_REMOVED_PROFILE_IMAGE';
export const SUPPLIER_DETAIL_FTP_USER_CLEAR = 'SUPPLIER_DETAIL_FTP_USER_CLEAR';
export const SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_RECEIVE = 'SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_RECEIVE';
export const SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_REQUEST = 'SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_REQUEST';
export const SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE = 'SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE';
export const SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE_SUCCESS = 'SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE_SUCCESS';
export const SUPPLIER_DETAIL_FTP_ADD = 'SUPPLIER_DETAIL_FTP_ADD';
export const SUPPLIER_DETAIL_FTP_ADD_SUCCESS = 'SUPPLIER_DETAIL_FTP_ADD_SUCCESS';

const requestSupplier = () => {
    return {
        type: SUPPLIER_DETAIL_OVERVIEW_REQUEST_DETAILS,
    };
};

export const updateSupplierDetail = (
    formData: SupplierDetailOverviewFormValues,
    isDirty: boolean,
    isValid: boolean,
    errors: FormikErrors<SupplierDetailOverviewFormValues>
) => {
    return {
        type: SUPPLIER_DETAIL_OVERVIEW_UPDATE,
        formData,
        isDirty,
        isValid,
        errors,
    };
};

const overviewError = (errorData?: HandledError<EmailErrorCode>[]) => ({
    type: SUPPLIER_DETAIL_OVERVIEW_ERROR,
    errorData,
});

const bankAccountError = (errorData?: HandledError<BankAccountErrorCode>[]) => ({
    type: SUPPLIER_DETAIL_BANK_ACCOUNT_ERROR,
    errorData,
});

const receiveSupplierDetails = (supplierDetails: SupplierDetail, locale: Locale) => {
    const supplier = getSupplierFormValues(supplierDetails, locale);
    const bankAccounts = supplierDetails.bankAccounts;

    return {
        type: SUPPLIER_DETAIL_OVERVIEW_RECEIVE_DETAILS,
        supplier,
        bankAccounts,
    };
};

export const clearSupplierFtpUser = () => {
    return {
        type: SUPPLIER_DETAIL_FTP_USER_CLEAR,
    };
};

const receiveNewBankAccount = (newBankAccount: BankAccount) => {
    return {
        type: SUPPLIER_DETAIL_BANK_ACCOUNT_CREATED,
        newBankAccount,
    };
};

const receiveUpdatedBankAccount = (bankAccount: BankAccount) => {
    return {
        type: SUPPLIER_DETAIL_BANK_ACCOUNT_UPDATED,
        bankAccount,
    };
};

const deleteBankAccountFinished = (id: number) => {
    return {
        type: SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE_FINISHED,
        id,
    };
};

const fileUploadFinished = (bankAccountId: number, media: Media) => {
    return {
        type: SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED,
        bankAccountId,
        media,
    };
};

const getSupplierFormValues = (supplier: SupplierDetail, locale: Locale): SupplierDetailOverviewFormValues => {
    const addresses = supplier.addresses.map(address => {
        return {
            ...address,
            country: getCountry(locale, address.country),
        };
    });

    const notifications =
        supplier.notificationSettings?.map(notificationSetting => ({
            ...notificationSetting,
            overrideEmail: !!notificationSetting.recipientEmail,
            recipientEmail: notificationSetting.recipientEmail || '',
        })) ?? [];

    return {
        ...supplier,
        addresses,
        notificationSettings: notifications,
    };
};

const receiveWhitelistedCustomers = (whitelistedCustomer: WhitelistedCustomer[]) => {
    return {
        type: SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_RECEIVE,
        whitelistedCustomer,
    };
};

const profileUploadFinished = (media: Media) => {
    return {
        type: SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_FINISHED,
        media,
    };
};

const uploadNewFiles = (media: Media[], id: number) => {
    return (dispatch: Dispatch, getState: () => AppState, api: { api: Api }) => {
        const newFiles = media.reduce(
            (acc: File[], item: Media) => (item.file !== undefined ? [...acc, item.file] : acc),
            []
        );

        if (newFiles) {
            newFiles.forEach(file => {
                uploadFile(id, file)(dispatch, getState, api);
            });
        }
    };
};

export const fetchSupplierDetails = (supplierId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestSupplier());
        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.get(supplierId);
        if (response.success) {
            dispatch(receiveSupplierDetails(response.result, bestLocaleSelector(getState())));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteFtpUser = (id?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        if (!id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.deleteFtpUser(id);

        dispatch(clearSupplierFtpUser());

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.ftpUserDeleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const saveSupplierDetail = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_OVERVIEW_SAVE });
        const request = supplierDetailOverviewRequestSelector(getState());
        if (!request) {
            return SaveResult.Error;
        }
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;

        let response: ApiResult<SupplierDetail>;
        if (supplierId) {
            response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.patchDetail(
                supplierId,
                request
            );
        } else {
            response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.createDetail(request);
        }

        if (response.success) {
            dispatch(receiveSupplierDetails(response.result, bestLocaleSelector(getState())));
            return SaveResult.Success;
        }

        // The error handler answers with a list either way, so an empty one means the
        // rejection could not be pinned on a field. Reporting that as a form error
        // leaves the user with a red tab and nothing marked in it.
        if (response.handledErrors?.length) {
            dispatch(overviewError(response.handledErrors));
            return SaveResult.FormErrors;
        }

        dispatch(Snackbar.enqueue(serverErrorNotification(response.error)));

        return SaveResult.ErrorReported;
    };
};

export const saveNewFtpPassword = (newFtpPassword: string, supplierId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE });

        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.saveNewFtpPassword(
            supplierId,
            newFtpPassword
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.ftpPasswordChanged')));
            dispatch({ type: SUPPLIER_DETAIL_NEW_FTP_PASSWORD_SAVE_SUCCESS, newFtpPassword });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const addFtp = (ftpPassword: string, supplierId?: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_FTP_ADD });

        if (!supplierId || !ftpPassword) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.addFtp(
            supplierId,
            ftpPassword
        );

        if (response.success) {
            dispatch({ type: SUPPLIER_DETAIL_FTP_ADD_SUCCESS, ftpUser: response.result, ftpPassword });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.ftpUserAdded')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const addNewBankAccount = (bankAccount: BankAccountFormValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_BANK_ACCOUNT_ADD });

        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.addBankAccount(
            supplierId,
            bankAccount
        );

        if (response.success) {
            dispatch(receiveNewBankAccount(response.result));
            uploadNewFiles(bankAccount.media, response.result.id)(dispatch, getState, { api });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountAdded')));
        } else {
            if (response.errorHandled) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountValidationFailed')));
                dispatch(bankAccountError(response.handledErrors));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountError')));
            }
        }

        return response;
    };
};

export const updateExistingBankAccount = (bankAccount: BankAccountFormValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        const id = bankAccount.id;
        if (!supplierId || !id) {
            return;
        }

        bankAccount.media.filter(media => media.id > 0);
        delete bankAccount.id;
        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.updateBankAccount(
            supplierId,
            bankAccount,
            id
        );

        if (response.success) {
            dispatch(receiveUpdatedBankAccount(response.result));
            uploadNewFiles(bankAccount.media, response.result.id)(dispatch, getState, { api });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountSaved')));
        } else {
            if (response.errorHandled) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountValidationFailed')));
                dispatch(bankAccountError(response.handledErrors));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountError')));
            }
        }

        return response;
    };
};

export const deleteBankAccount = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_BANK_ACCOUNT_DELETE });

        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.deleteBankAccount(
            supplierId,
            id
        );

        if (response.success) {
            dispatch(deleteBankAccountFinished(id));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountDeleteSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const uploadFile = (id: number, file: File) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_BANK_ACCOUNT_UPLOAD_FILE });

        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.uploadFile(
            supplierId,
            id,
            file
        );

        if (response.success) {
            dispatch(fileUploadFinished(id, response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.fileUploadSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.fileUploadError')));
        }
    };
};

export const fetchWhitelistedCustomers = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_WHITELISTED_CUSTOMERS_REQUEST });

        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.searchWhitelistedCustomers(
            supplierId,
            searchParams
        );

        if (response.success) {
            dispatch(receiveWhitelistedCustomers(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const uploadProfileImage = (file: File) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_DETAIL_OVERVIEW_UPLOAD_PROFILE_IMAGE });

        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.uploadProfileImage(
            supplierId,
            file
        );

        if (response.success) {
            dispatch(profileUploadFinished(response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.logoUploadSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.logoUploadError')));
        }
    };
};

export const removeProfileImage = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const supplierId = getState().contactsAndOrganizations.suppliers.supplierDetail.overview.supplier?.id;
        if (!supplierId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Overview.removeProfileImage(
            supplierId
        );

        if (response.success) {
            dispatch({ type: SUPPLIER_DETAIL_OVERVIEW_REMOVED_PROFILE_IMAGE });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.logoRemoveSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.logoRemoveError')));
        }
    };
};
